import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import BpsGlobalLogo from '../Logo/BpsGlobal.jpeg';

// Helper function to load image as base64
const loadImageAsBase64 = (src) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/jpeg'));
    };
    img.onerror = reject;
    img.src = src;
  });
};

// Convert number to words for AED
const numberToWords = (num) => {
  if (num === 0) return 'Zero';
  const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
    'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
  const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];

  const convertGroup = (n) => {
    if (n === 0) return '';
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
    return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' and ' + convertGroup(n % 100) : '');
  };

  const absNum = Math.abs(num);
  const wholePart = Math.floor(absNum);
  const decimalPart = Math.round((absNum - wholePart) * 100);

  let result = '';
  if (wholePart >= 10000000) {
    result += convertGroup(Math.floor(wholePart / 10000000)) + ' Crore ';
    const remaining = wholePart % 10000000;
    if (remaining >= 100000) result += convertGroup(Math.floor(remaining / 100000)) + ' Lakh ';
    const rem2 = remaining % 100000;
    if (rem2 >= 1000) result += convertGroup(Math.floor(rem2 / 1000)) + ' Thousand ';
    const rem3 = rem2 % 1000;
    if (rem3 > 0) result += convertGroup(rem3);
  } else if (wholePart >= 100000) {
    result += convertGroup(Math.floor(wholePart / 100000)) + ' Lakh ';
    const remaining = wholePart % 100000;
    if (remaining >= 1000) result += convertGroup(Math.floor(remaining / 1000)) + ' Thousand ';
    const rem = remaining % 1000;
    if (rem > 0) result += convertGroup(rem);
  } else if (wholePart >= 1000) {
    result += convertGroup(Math.floor(wholePart / 1000)) + ' Thousand ';
    const remaining = wholePart % 1000;
    if (remaining > 0) result += convertGroup(remaining);
  } else {
    result = convertGroup(wholePart);
  }

  result = result.trim();
  if (decimalPart > 0) {
    result += ' and ' + decimalPart + ' fils';
  }

  return result;
};

/**
 * Generate Local Purchase Order (LPO) PDF
 */
export const generateLocalPOPdf = async (poData, details, supplier, branchDetails, termsDetails) => {
  const doc = new jsPDF('p', 'mm', 'a4', true);
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 10;
  const contentWidth = pageWidth - margin * 2;

  // Colors
  const navy = [30, 58, 95]; // #1e3a5f
  const darkText = [30, 41, 59];
  const grayText = [100, 116, 139];

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  let yPos = margin;

  // === LOGO & COMPANY HEADER ===
  try {
    const logoBase64 = await loadImageAsBase64(BpsGlobalLogo);
    doc.addImage(logoBase64, 'JPEG', margin, yPos, 24, 24);
  } catch (e) {
    console.error('Logo load failed:', e);
  }

  // Company Name - next to logo
  const companyName = branchDetails.companyName || branchDetails.branchName || '';
  if (companyName) {
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...navy);
    doc.text(companyName, margin + 27, yPos + 10);
  }

  // Branch details - right side
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...navy);
  if (branchDetails.branchName) {
    doc.text(branchDetails.branchName, pageWidth - margin, yPos + 4, { align: 'right' });
  }
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...darkText);
  let rightY = yPos + 9;
  if (branchDetails.addressLine1) { doc.text(branchDetails.addressLine1, pageWidth - margin, rightY, { align: 'right' }); rightY += 4; }
  if (branchDetails.addressLine2) { doc.text(branchDetails.addressLine2, pageWidth - margin, rightY, { align: 'right' }); rightY += 4; }
  if (branchDetails.location || branchDetails.pincode) { doc.text((branchDetails.location || '') + (branchDetails.pincode ? ' - ' + branchDetails.pincode : ''), pageWidth - margin, rightY, { align: 'right' }); rightY += 4; }
  if (branchDetails.tradeLicenseNo) { doc.text('Trade License No: ' + branchDetails.tradeLicenseNo, pageWidth - margin, rightY, { align: 'right' }); rightY += 4; }
  if (branchDetails.gstNumber) { doc.text('TRN: ' + branchDetails.gstNumber, pageWidth - margin, rightY, { align: 'right' }); rightY += 4; }

  yPos += 28;

  // Separator line
  doc.setDrawColor(...navy);
  doc.setLineWidth(0.5);
  doc.line(margin, yPos, pageWidth - margin, yPos);
  yPos += 6;

  // === TITLE ===
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...navy);
  doc.text('Local Purchase Order (LPO)', pageWidth / 2, yPos, { align: 'center' });
  yPos += 3;
  doc.setLineWidth(0.8);
  const titleWidth = doc.getTextWidth('Local Purchase Order (LPO)');
  doc.line((pageWidth - titleWidth) / 2, yPos, (pageWidth + titleWidth) / 2, yPos);
  yPos += 8;

  // === PO DETAILS (left) + SUPPLIER (right) ===
  const halfWidth = contentWidth / 2 - 2;
  const boxStartY = yPos;
  const lineH = 4.5;

  // Left box: Purchase Order Details
  doc.setDrawColor(...navy);
  doc.setLineWidth(0.4);
  doc.rect(margin, boxStartY, halfWidth, 42);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...navy);
  doc.text('PURCHASE ORDER DETAILS', margin + 3, boxStartY + 5);
  doc.line(margin, boxStartY + 7, margin + halfWidth, boxStartY + 7);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...darkText);
  doc.setFontSize(7.5);
  let leftY = boxStartY + 12;
  const poLabelX = margin + 3;
  const poDataX = margin + 38;
  doc.setFont('helvetica', 'bold'); doc.text('PO No.:', poLabelX, leftY); doc.setFont('helvetica', 'normal'); doc.text(poData.PurchaseOrderNo || '-', poDataX, leftY); leftY += lineH;
  doc.setFont('helvetica', 'bold'); doc.text('PO Date:', poLabelX, leftY); doc.setFont('helvetica', 'normal'); doc.text(formatDate(poData.PurchaseOrderDate), poDataX, leftY); leftY += lineH;
  doc.setFont('helvetica', 'bold'); doc.text('Delivery Date:', poLabelX, leftY); doc.setFont('helvetica', 'normal'); doc.text(poData.RequiredDeliveryDate ? formatDate(poData.RequiredDeliveryDate) : '-', poDataX, leftY); leftY += lineH;
  doc.setFont('helvetica', 'bold'); doc.text('Payment Terms:', poLabelX, leftY); doc.setFont('helvetica', 'normal'); doc.text(poData.PaymentTerms || '-', poDataX, leftY); leftY += lineH;
  doc.setFont('helvetica', 'bold'); doc.text('Delivery Location:', poLabelX, leftY); doc.setFont('helvetica', 'normal'); doc.text(poData.DeliveryLocation || '-', poDataX, leftY); leftY += lineH;
  doc.setFont('helvetica', 'bold'); doc.text('Requested By:', poLabelX, leftY); doc.setFont('helvetica', 'normal'); doc.text(poData.RequestedBy || '-', poDataX, leftY);

  // Right box: Supplier Details
  const rightX = margin + halfWidth + 4;
  doc.setDrawColor(...navy);
  doc.rect(rightX, boxStartY, halfWidth, 42);

  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...navy);
  doc.text('SUPPLIER DETAILS', rightX + 3, boxStartY + 5);
  doc.line(rightX, boxStartY + 7, rightX + halfWidth, boxStartY + 7);

  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...darkText);
  doc.setFontSize(7.5);
  let sY = boxStartY + 12;
  const supLabelX = rightX + 3;
  const supDataX = rightX + 30;
  const supplierName = poData.MasterName || supplier?.Name || '-';
  const supplierAddr = (supplier?.AddressLine1 || '') + (supplier?.AddressLine2 ? ', ' + supplier.AddressLine2 : '') + (supplier?.Location ? ', ' + supplier.Location : '');
  doc.setFont('helvetica', 'bold'); doc.text('Supplier Name:', supLabelX, sY); doc.setFont('helvetica', 'normal'); doc.text(supplierName, supDataX, sY); sY += lineH;
  doc.setFont('helvetica', 'bold'); doc.text('Address:', supLabelX, sY); doc.setFont('helvetica', 'normal');
  const addrLines = doc.splitTextToSize(supplierAddr || '-', halfWidth - 33);
  doc.text(addrLines, supDataX, sY); sY += lineH * Math.max(addrLines.length, 1);
  doc.setFont('helvetica', 'bold'); doc.text('Supplier TRN:', supLabelX, sY); doc.setFont('helvetica', 'normal'); doc.text(supplier?.VATNumber || '-', supDataX, sY); sY += lineH;
  doc.setFont('helvetica', 'bold'); doc.text('Trade License No.:', supLabelX, sY); doc.setFont('helvetica', 'normal'); doc.text(supplier?.TradeLicenseNo || supplier?.LicenseNo || '-', supDataX, sY); sY += lineH;
  doc.setFont('helvetica', 'bold'); doc.text('Contact Person:', supLabelX, sY); doc.setFont('helvetica', 'normal'); doc.text(supplier?.ContactPerson || '-', supDataX, sY); sY += lineH;
  doc.setFont('helvetica', 'bold'); doc.text('Tel / Email:', supLabelX, sY); doc.setFont('helvetica', 'normal'); doc.text((supplier?.ContactNumber1 || '') + ' / ' + (supplier?.Email || ''), supDataX, sY);

  yPos = boxStartY + 46;

  // === ITEMS TABLE ===
  const tableBody = details.map((d, idx) => {
    const itemName = d.ItemName || '-';
    const qty = parseFloat(d.Quantity) || 0;
    const rate = parseFloat(d.Rate) || 0;
    const taxableAmount = parseFloat(d.TaxableAmount) || (qty * rate);
    const vatRate = parseFloat(d.VATRate) || 5;
    const vatAmount = parseFloat(d.VATAmount) || (taxableAmount * vatRate / 100);
    const totalAmount = parseFloat(d.TotalAmount) || (taxableAmount + vatAmount);
    return [idx + 1, itemName, d.HSNCode || '-', qty, rate.toFixed(2), taxableAmount.toFixed(2), vatAmount.toFixed(2), totalAmount.toFixed(2)];
  });

  autoTable(doc, {
    startY: yPos,
    head: [['S.No', 'Description of Goods / Services', 'HS / Product Code', 'Qty', 'Unit Price\n(AED)', 'Taxable Amount\n(AED)', 'VAT @5%\n(AED)', 'Total\n(AED)']],
    body: tableBody,
    theme: 'grid',
    headStyles: { fillColor: navy, textColor: [255, 255, 255], fontSize: 7, fontStyle: 'bold', halign: 'center', cellPadding: 2 },
    bodyStyles: { fontSize: 7.5, cellPadding: 2, textColor: darkText },
    columnStyles: {
      0: { halign: 'center', cellWidth: 12 },
      1: { halign: 'left' },
      2: { halign: 'center', cellWidth: 22 },
      3: { halign: 'center', cellWidth: 14 },
      4: { halign: 'right', cellWidth: 22 },
      5: { halign: 'right', cellWidth: 24 },
      6: { halign: 'right', cellWidth: 20 },
      7: { halign: 'right', cellWidth: 22 }
    },
    tableWidth: contentWidth,
    margin: { left: margin, right: margin },
    styles: { lineColor: navy, lineWidth: 0.2 }
  });

  yPos = doc.lastAutoTable.finalY + 5;

  // === TOTALS ===
  const subtotal = details.reduce((sum, d) => sum + (parseFloat(d.TaxableAmount) || 0), 0);
  const totalVAT = details.reduce((sum, d) => sum + (parseFloat(d.VATAmount) || 0), 0);
  const freightAmount = parseFloat(poData.FreightAmount) || 0;
  const packingAmount = parseFloat(poData.PackingAmount) || 0;
  const freightVATAmount = parseFloat(poData.FreightVATAmount) || 0;
  const packingVATAmount = parseFloat(poData.PackingVATAmount) || 0;
  const grandTotal = parseFloat(poData.TotalAmount) || (subtotal + totalVAT + freightAmount + freightVATAmount + packingAmount + packingVATAmount);

  const totalsX = pageWidth - margin - 75;
  doc.setDrawColor(...navy);
  doc.setLineWidth(0.3);

  // Subtotal (Taxable Amount)
  doc.rect(totalsX, yPos, 75, 7);
  doc.setFontSize(7.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(...darkText);
  doc.text('Subtotal (Taxable Amount):', totalsX + 2, yPos + 5);
  doc.text(subtotal.toFixed(2), totalsX + 73, yPos + 5, { align: 'right' });

  // Total VAT @5%
  doc.rect(totalsX, yPos + 7, 75, 7);
  doc.setFont('helvetica', 'normal');
  doc.text('Total VAT @ 5%:', totalsX + 2, yPos + 12);
  doc.text(totalVAT.toFixed(2), totalsX + 73, yPos + 12, { align: 'right' });

  // Grand Total
  doc.setFillColor(240, 244, 255);
  doc.rect(totalsX, yPos + 14, 75, 8, 'FD');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
  doc.text('Grand Total (AED):', totalsX + 2, yPos + 20);
  doc.text(grandTotal.toFixed(2), totalsX + 73, yPos + 20, { align: 'right' });

  yPos += 28;

  // === AMOUNT IN WORDS ===
  const amountInWords = 'UAE Dirhams ' + numberToWords(grandTotal) + ' Only.';
  doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(...navy);
  doc.text('Amount in Words:', margin, yPos);
  doc.setFont('helvetica', 'italic'); doc.setTextColor(...darkText);
  const wordsLines = doc.splitTextToSize(amountInWords, contentWidth - 30);
  doc.text(wordsLines, margin + 30, yPos);
  yPos += wordsLines.length * 4 + 4;

  // === DELIVERY & INSPECTION + AUTHORISED SIGNATORY ===
  if (yPos > 240) { doc.addPage(); yPos = margin; }

  doc.setDrawColor(...navy);
  doc.rect(margin, yPos, halfWidth, 30);
  doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(...navy);
  doc.text('DELIVERY & INSPECTION', margin + 3, yPos + 5);
  doc.line(margin, yPos + 7, margin + halfWidth, yPos + 7);

  doc.setFont('helvetica', 'normal'); doc.setTextColor(...darkText); doc.setFontSize(7.5);
  let dY = yPos + 12;
  doc.text('Goods/services must match this order in specification,', margin + 3, dY); dY += 3.5;
  doc.text('quantity and quality.', margin + 3, dY); dY += 3.5;
  doc.text('Delivery note / service completion report required on', margin + 3, dY); dY += 3.5;
  doc.text('receipt.', margin + 3, dY);

  doc.setDrawColor(...navy);
  doc.rect(rightX, yPos, halfWidth, 30);
  doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(...navy);
  doc.text('AUTHORISED SIGNATORY', rightX + 3, yPos + 5);
  doc.line(rightX, yPos + 7, rightX + halfWidth, yPos + 7);

  doc.setFont('helvetica', 'bold'); doc.setTextColor(...darkText); doc.setFontSize(7.5);
  doc.text('For ' + (branchDetails.companyName || branchDetails.branchName || ''), rightX + 3, yPos + 12);
  doc.setFont('helvetica', 'italic'); doc.setTextColor(...grayText);
  doc.text('Signature & Company Stamp', rightX + 3, yPos + 27);

  yPos += 34;

  // === PURCHASE TERMS & CONDITIONS ===
  const allTerms = termsDetails || [];
  const localTerms = allTerms.filter(t => !t.IsInternational);
  if (localTerms.length > 0) {
    if (yPos > 250) { doc.addPage(); yPos = margin; }
    doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(...navy);
    doc.text('Purchase Terms & Conditions', margin, yPos);
    yPos += 2;
    doc.setLineWidth(0.4);
    doc.line(margin, yPos, margin + 50, yPos);
    yPos += 4;

    doc.setFont('helvetica', 'normal'); doc.setTextColor(...darkText); doc.setFontSize(6.5);
    localTerms.forEach((t, i) => {
      if (yPos > 280) { doc.addPage(); yPos = margin; }
      const lines = doc.splitTextToSize((i + 1) + '. ' + (t.Name || ''), contentWidth);
      doc.text(lines, margin, yPos);
      yPos += lines.length * 3.2;
    });
  }

  // === FOOTER NOTE ===
  yPos += 4;
  if (yPos > 270) { doc.addPage(); yPos = margin; }
  doc.setFillColor(239, 246, 255);
  doc.setDrawColor(147, 197, 253);
  doc.roundedRect(margin, yPos, contentWidth, 8, 1, 1, 'FD');
  doc.setFontSize(6); doc.setFont('helvetica', 'normal'); doc.setTextColor(...navy);
  doc.text('This PO is issued by ' + (branchDetails.companyName || '') + ' in accordance with UAE commercial practice and Federal Decree-Law No.(8) of 2017 on', margin + 2, yPos + 3.5);
  doc.text('Value Added Tax. The Supplier shall issue a valid FTA-compliant Tax Invoice quoting its TRN for all amounts billed against this order.', margin + 2, yPos + 6.5);

  // === WEBSITE FOOTER ===
  yPos += 14;
  if (yPos > 280) { doc.addPage(); yPos = margin; }
  doc.setFontSize(8); doc.setFont('helvetica', 'bold'); doc.setTextColor(...navy);
  doc.text('www.bpsglobaltrading.com', pageWidth / 2, yPos, { align: 'center' });

  return doc;
};

/**
 * Download Local PO as PDF
 */
export const downloadLocalPOPdf = async (poData, details, supplier, branchDetails, termsDetails) => {
  const doc = await generateLocalPOPdf(poData, details, supplier, branchDetails, termsDetails);
  doc.save('LPO_' + (poData.PurchaseOrderNo || 'draft') + '.pdf');
};

/**
 * Share Local PO PDF via WhatsApp
 */
export const shareLocalPOViaWhatsApp = async (poData, details, supplier, branchDetails, termsDetails, phoneNumber) => {
  try {
    const doc = await generateLocalPOPdf(poData, details, supplier, branchDetails, termsDetails);
    const pdfBlob = doc.output('blob');
    const poNo = poData.PurchaseOrderNo || 'draft';
    const totalAmount = parseFloat(poData.TotalAmount || 0).toFixed(2);
    const supplierName = poData.MasterName || supplier?.Name || '';

    const message = `Hello ${supplierName},\n\nPlease find the Local Purchase Order ${poNo} for AED ${totalAmount}.\n\nKindly acknowledge receipt and confirm.\n\nThank you.`;

    if (navigator.share && navigator.canShare && navigator.canShare({ files: [new File([pdfBlob], 'po.pdf', { type: 'application/pdf' })] })) {
      const file = new File([pdfBlob], `LPO_${poNo.replace(/\//g, '_')}.pdf`, { type: 'application/pdf' });
      await navigator.share({ title: `LPO ${poNo}`, text: message, files: [file] });
      return { success: true, method: 'native' };
    } else {
      const encodedMessage = encodeURIComponent(message);
      const whatsappUrl = phoneNumber
        ? `https://wa.me/${phoneNumber.replace(/[^0-9]/g, '')}?text=${encodedMessage}`
        : `https://wa.me/?text=${encodedMessage}`;

      const fileName = `LPO_${poNo.replace(/\//g, '_')}.pdf`;
      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);

      window.open(whatsappUrl, '_blank');
      return { success: true, method: 'fallback', message: 'PDF downloaded. Please attach it manually in WhatsApp.' };
    }
  } catch (error) {
    console.error('Error sharing LPO via WhatsApp:', error);
    return { success: false, error: error.message };
  }
};
